import React, { useEffect, useMemo, useState } from 'react';
import { connect } from 'react-redux';
import styled from 'styled-components';

import Modal from '../../../Components/UI/Modal/Modal';
import QuoteCostEditor, { quoteFromEditorValue } from '../../../Components/Project/QuoteCostEditor';
import QuoteRevenuePreview from '../../../Components/Project/QuoteRevenuePreview';
import { emptyMaterialLine, LABOUR_MODES, labourModeFromContract, taxModeFromContract } from '../../../Domain/quote';
import { sendModificaionArtisan } from '../../../Store/Actions/actionOffer';
import { useI18n } from '../../../i18n/I18nProvider';

const materialDraftFromContract = (contract = {}) => {
  const materials = Array.isArray(contract.material_list)
    ? contract.material_list.map((item) => ({
        material_name: item?.material_name || '',
        material_quantity: String(item?.material_quantity ?? ''),
        material_price: String(item?.material_price ?? ''),
      }))
    : [];

  const labourMode = labourModeFromContract(contract);
  const legacyWage = String(contract.wage ?? '');
  return {
    materials: materials.length ? materials : [emptyMaterialLine()],
    labourMode,
    labourHours: labourMode === LABOUR_MODES.HOURS ? String(contract.labour_hours ?? '') : '',
    hourlyRate: labourMode === LABOUR_MODES.HOURS ? String(contract.hourly_rate ?? '') : '',
    wage: labourMode === LABOUR_MODES.FIXED ? legacyWage : '',
    travelCost: String(contract.travel_cost ?? '0'),
    taxMode: taxModeFromContract(contract),
  };
};

const ModificationModal = ({ showModal, handleClick, demand, firebase, loading, sendModification }) => {
  const { pick } = useI18n();
  const contract = demand?.contract;
  const profile = firebase?.profile || {};
  const userId = firebase?.auth?.uid;
  const opened = showModal === 'open';

  const [response, setResponse] = useState('');
  const [phone, setPhone] = useState('');
  const [startDate, setStartDate] = useState('');
  const [estimatedDuration, setEstimatedDuration] = useState('');
  const [validityDays, setValidityDays] = useState('30');
  const [costs, setCosts] = useState(() => materialDraftFromContract());
  const [editorSession, setEditorSession] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [showCostValidation, setShowCostValidation] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!opened || !contract) return;
    setResponse('');
    setPhone(contract.artisan_phone_number || profile.phone_number || '');
    setStartDate(contract.start_date || '');
    setEstimatedDuration(contract.estimated_duration || '');
    setValidityDays(String(contract.quote_validity_days || 30));
    const nextCosts = materialDraftFromContract(contract);
    setCosts(nextCosts);
    setEditorSession((session) => session + 1);
    setShowCostValidation(false);
    setError('');
  }, [opened, demand?.demand_id]); // Keep edits intact if the live profile refreshes while the modal is open.

  // Revision preview and submission derive from the same cost state.
  const quote = useMemo(() => quoteFromEditorValue(costs), [costs]);

  const close = () => { if (!submitting) handleClick?.(); };

  const submit = async () => {
    if (submitting) return;
    setShowCostValidation(true);
    if (!userId || !demand?.demand_id || !contract) {
      return setError(pick('La session, le projet ou le devis est introuvable.', 'The session, project or quote could not be found.'));
    }
    if (response.trim().length < 10) {
      return setError(pick('Expliquez les modifications apportées en au moins 10 caractères.', 'Explain the changes made in at least 10 characters.'));
    }
    if (!phone.trim() || !startDate || !estimatedDuration.trim()) {
      return setError(pick('Renseignez le téléphone, la date de début et la durée estimée.', 'Enter the phone number, start date and estimated duration.'));
    }
    if (!Number.isInteger(Number(validityDays)) || Number(validityDays) < 1 || Number(validityDays) > 365) {
      return setError(pick('La durée de validité doit être comprise entre 1 et 365 jours.', 'Quote validity must be between 1 and 365 days.'));
    }
    if (!quote.valid) {
      return setError(quote.errors[0] || pick('Vérifiez le détail des coûts.', 'Check the cost breakdown.'));
    }

    setError('');
    setSubmitting(true);
    try {
      const success = await sendModification({
        demandId: demand.demand_id,
        artisanId: userId,
        artisanFirstName: profile.firstName,
        artisanLastName: profile.lastName,
        artisanEmail: profile.email,
        artisanAddress: profile.address,
        artisanProfileImage: profile.profile_image,
        artisanBusinessName: contract.artisan_business_name || '',
        artisanSiret: contract.artisan_siret || '',
        artisanInsurance: contract.artisan_insurance || '',
        artisanCompanyLogo: contract.artisan_company_logo || '',
        artisanLegalForm: contract.artisan_legal_form || '',
        artisanVatNumber: contract.artisan_vat_number || '',
        artisanWebsite: contract.artisan_website || '',
        paymentTerms: contract.payment_terms || '',
        documentFooter: contract.document_footer || '',
        documentTemplateVersion: contract.document_template_version || 'legacy',
        descriptionOfContract: contract.description,
        modificationResponse: response.trim(),
        modificationAsked: demand.modification?.modification || '',
        startDate,
        estimatedDuration: estimatedDuration.trim(),
        quoteValidityDays: Number(validityDays),
        contactNumber: phone.trim(),
        materialList: quote.materialList,
        materialCost: quote.materialCost,
        labourMode: quote.labourMode,
        labourHours: quote.labourHours,
        hourlyRate: quote.hourlyRate,
        wage: quote.wage,
        travelCost: quote.travelCost,
        subtotalHt: quote.subtotalHt,
        vatRate: quote.vatRate,
        vatAmount: quote.vatAmount,
        taxLabel: quote.taxLabel,
        totalCost: quote.totalTtc,
      });
      if (success) close();
    } finally {
      setSubmitting(false);
    }
    return undefined;
  };

  return (
    <Modal opened={opened} close={close}>
      <Header>
        <div>
          <Eyebrow>{pick('Révision demandée', 'Revision requested')}</Eyebrow>
          <Title>{pick('Modifier et renvoyer le devis', 'Revise and resend the quote')}</Title>
          <Subtitle>{pick('Corrigez les lignes concernées. Le total est recalculé ici puis vérifié à nouveau par le serveur.', 'Edit the relevant lines. The total is recalculated here and verified again by the server.')}</Subtitle>
        </div>
        <CloseButton type="button" aria-label={pick('Fermer', 'Close')} onClick={close}>×</CloseButton>
      </Header>

      {!contract ? <EmptyState>{pick('Le devis précédent est introuvable.', 'The previous quote could not be found.')}</EmptyState> : (
        <>
          <RequestCard>
            <span>{pick('Demande du client', 'Client request')}</span>
            <p>{demand.modification?.modification || pick('Le client n’a pas ajouté de précision.', 'The client did not add any details.')}</p>
          </RequestCard>

          <Form>
            <Field $wide>
              <label htmlFor="modification-response">{pick('Votre réponse et les changements effectués', 'Your response and changes made')}</label>
              <textarea id="modification-response" value={response} onChange={(event) => setResponse(event.target.value)} placeholder={pick('Expliquez clairement ce qui a été corrigé dans le devis…', 'Clearly explain what was changed in the quote…')} maxLength={1500} />
              <Hint>{response.length}/1500</Hint>
            </Field>
            <Field><label htmlFor="modification-phone">{pick('Téléphone', 'Phone')}</label><input id="modification-phone" type="tel" value={phone} onChange={(event) => setPhone(event.target.value)} /></Field>
            <Field><label htmlFor="modification-date">{pick('Date de début prévue', 'Planned start date')}</label><input id="modification-date" type="date" value={startDate} onChange={(event) => setStartDate(event.target.value)} /></Field>
            <Field><label htmlFor="modification-duration">{pick('Durée estimée', 'Estimated duration')}</label><input id="modification-duration" value={estimatedDuration} onChange={(event) => setEstimatedDuration(event.target.value)} /></Field>
            <Field><label htmlFor="modification-validity">{pick('Validité du devis (jours)', 'Quote validity (days)')}</label><input id="modification-validity" type="number" min="1" max="365" value={validityDays} onChange={(event) => setValidityDays(event.target.value)} /></Field>
          </Form>

          <QuoteCostEditor key={editorSession} value={costs} onChange={setCosts} showValidation={showCostValidation} />
          <QuoteRevenuePreview totalTtc={quote.totalTtc} />

          <Transparency>
            <strong>{pick('Le devis précédent n’est pas modifié en place', 'The previous quote is not overwritten in place')}</strong>
            <span>{pick('La nouvelle version remplace le devis actif et conserve la demande du client ainsi que votre réponse dans l’historique du projet.', 'The new version replaces the active quote while keeping the client request and your response in the project history.')}</span>
          </Transparency>

          {error && <ErrorMessage role="alert">{error}</ErrorMessage>}
          <Actions>
            <SecondaryButton type="button" onClick={close} disabled={submitting || loading}>{pick('Annuler', 'Cancel')}</SecondaryButton>
            <PrimaryButton type="button" onClick={submit} disabled={submitting || loading}>{submitting ? pick('Envoi…', 'Sending…') : pick('Renvoyer le devis', 'Resend quote')}</PrimaryButton>
          </Actions>
        </>
      )}
    </Modal>
  );
};

const Header = styled.header`display:flex;align-items:flex-start;justify-content:space-between;gap:1rem;margin-bottom:1.2rem;min-width:0;>div{min-width:0;}@media(max-width:640px){position:sticky;top:0;z-index:3;margin:-1rem -1rem 1rem;padding:1rem;border-bottom:1px solid #eee7dc;background:rgba(255,255,255,.97);backdrop-filter:blur(12px);}`;
const Eyebrow = styled.div`margin-bottom:.35rem;color:#a88a5d;font-size:.76rem;font-weight:900;letter-spacing:.12em;text-transform:uppercase;`;
const Title = styled.h2`margin:0;color:#111;font-size:clamp(1.3rem,4vw,1.85rem);`;
const Subtitle = styled.p`margin:.4rem 0 0;color:#726b61;line-height:1.5;overflow-wrap:anywhere;`;
const CloseButton = styled.button`width:2.5rem;height:2.5rem;flex:0 0 auto;border:0;border-radius:50%;background:#f1ece5;color:#111;font:inherit;font-size:1.6rem;cursor:pointer;`;
const RequestCard = styled.section`padding:1rem;border:1px solid #edd28f;border-radius:.9rem;background:#fff9ea;color:#513d16;span{display:block;margin-bottom:.4rem;font-size:.74rem;font-weight:900;text-transform:uppercase;}p{margin:0;white-space:pre-wrap;line-height:1.55;overflow-wrap:anywhere;}`;
const Form = styled.div`display:grid;grid-template-columns:repeat(2,minmax(0,1fr));gap:.85rem;margin:1rem 0;@media(max-width:580px){grid-template-columns:1fr;}`;
const Field = styled.div`grid-column:${({ $wide }) => ($wide ? '1 / -1' : 'auto')};display:grid;gap:.38rem;label{color:#222;font-weight:800;}input,textarea{width:100%;min-width:0;box-sizing:border-box;padding:.78rem;border:1px solid #ddd3c4;border-radius:.75rem;color:#111;background:#fff;font:inherit;font-size:1rem;}textarea{min-height:7.5rem;resize:vertical;line-height:1.5;}`;
const Hint = styled.span`justify-self:end;color:#7a8790;font-size:.76rem;`;
const Transparency = styled.div`display:grid;gap:.25rem;margin-top:1rem;padding:.85rem 1rem;border:1px solid #cfe4d7;border-radius:.85rem;background:#edf8f1;strong{color:#1f6b43;}span{color:#49665a;font-size:.78rem;line-height:1.5;}`;
const ErrorMessage = styled.p`margin:.8rem 0 0;color:#a52e27;font-size:.88rem;`;
const EmptyState = styled.p`margin:0;padding:1rem;border-radius:.8rem;background:#faf8f4;color:#66615b;`;
const Actions = styled.div`display:flex;justify-content:flex-end;gap:.7rem;margin-top:1.25rem;@media(max-width:520px){display:grid;grid-template-columns:1fr;}`;
const BaseButton = styled.button`min-height:2.9rem;padding:.72rem 1.05rem;border-radius:999px;font:inherit;font-weight:800;cursor:pointer;box-sizing:border-box;&:disabled{opacity:.55;cursor:wait;}@media(max-width:520px){width:100%;}`;
const PrimaryButton = styled(BaseButton)`border:1px solid #c0a375;background:#c0a375;color:#111;`;
const SecondaryButton = styled(BaseButton)`border:1px solid #e7dfd2;background:#fff;color:#111;`;

const mapStateToProps = ({ firebase, demands }) => ({ firebase, loading: demands?.loading });
const mapDispatchToProps = (dispatch) => ({ sendModification: (contract) => dispatch(sendModificaionArtisan(contract)) });
export default connect(mapStateToProps, mapDispatchToProps)(ModificationModal);
